import { AlertTriangle, ClipboardList, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { WorkflowStepDto } from '@/features/workflows/schemas/workflowSchemas';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import { useCreateQuestionnaire } from '../api/useCreateQuestionnaire';
import { useListQuestionnaires } from '../api/useListQuestionnaires';
import {
  parseQuestionnaireStepSettings,
  writeQuestionnaireStepSettings,
} from '../lib/questionnaireStepSettings';
import { QuestionnaireFeatureIcons } from './QuestionnaireFeatureIcons';

interface Props {
  step: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  readOnly?: boolean;
  /**
   * Questionnaire ids attached to OTHER steps of this workflow.
   *
   * A workflow may hold several Questionnaire steps, but not two asking the SAME questionnaire — the
   * applicant would be shown identical questions twice, and both steps would write answers under the
   * same field ids, so the session would carry two indistinguishable copies. Those are excluded from
   * the pick list rather than rejected after the fact.
   */
  takenQuestionnaireIds?: readonly string[];
  /** Whether the workflow has edits not yet persisted. */
  isDirty?: boolean;
  /** Persists the workflow. Called before leaving for the questionnaire builder. */
  onSave?: () => void;
}

/**
 * What a Questionnaire step shows instead of the Method / Risk / Return Data tabs.
 *
 * A questionnaire step calls no provider — it collects answers — so none of those tabs has
 * anything to show, exactly as with Document Template. Same shape as `TemplateStepPanel`, which is
 * also why this component lives in its own feature and is imported by the workflow editor: the
 * feature that owns the domain owns the panel, and the editor composes it.
 *
 * ONLY PUBLISHED QUESTIONNAIRES CAN BE ATTACHED. A draft is by definition still being written, and
 * a workflow that went live pointing at one would ask applicants an unfinished set of questions.
 */
export function QuestionnaireStepPanel({
  step,
  onUpdate,
  readOnly = false,
  takenQuestionnaireIds = [],
  isDirty = false,
  onSave,
}: Props) {
  const navigate = useNavigate();
  const settings = parseQuestionnaireStepSettings(step.settings);
  const { data, isLoading, error } = useListQuestionnaires({ page: 1, pageSize: 100 });
  const createQuestionnaire = useCreateQuestionnaire();

  /**
   * Creates a questionnaire and opens its builder — the shortcut out of this panel when none of the
   * published ones fit.
   *
   * SAVES THE WORKFLOW FIRST when it is dirty. The builder is a different route, so leaving unmounts
   * this editor and takes every unsaved edit with it — including the Questionnaire step just added to
   * get here. Same guard `TemplateStepPanel` uses before opening the template designer.
   *
   * The new questionnaire is NOT auto-attached: it is created as a Draft with no questions, and only
   * published questionnaires can be attached. Attaching an empty draft would point this step at a form
   * with nothing in it.
   */
  function createAndOpen() {
    if (isDirty) onSave?.();

    createQuestionnaire.mutate(
      { name: 'New Questionnaire' },
      {
        onSuccess: (created) => navigate(`/questionnaires/${created.id}/builder`),
        onError: () => toast.error('Failed to create questionnaire'),
      },
    );
  }

  // Set while swapping the attached questionnaire. Switching must not require detaching first —
  // that loses the link if you change your mind.
  const [isPicking, setIsPicking] = useState(false);

  const all = data?.items ?? [];
  // Published, and not already asked by a sibling step. THIS step's own attachment is not excluded —
  // it has to stay listed so the current selection is visible when swapping.
  const taken = new Set(takenQuestionnaireIds);
  const published = all.filter((q) => q.status === 'Active' && !taken.has(q.id));

  const attached = all.find((q) => q.id === settings.questionnaireId);

  /**
   * Attaching also sets the step's DISPLAY NAME to the questionnaire's.
   *
   * That is what carries the name beyond this editor. `displayName` is persisted on the workflow step
   * and copied onto the session step when a run starts (StartSessionCommandHandler), so it is what
   * labels the step chip and the accordion title on the admin's session view, and the row in the
   * welcome overview. Without it, every questionnaire step read "Questionnaire" everywhere outside
   * this panel — and with two of them in one workflow, a reviewer could not tell which answers
   * belonged to which.
   */
  function attach(id: string, name: string) {
    onUpdate({
      displayName: name,
      settings: writeQuestionnaireStepSettings(step.settings, {
        questionnaireId: id,
        questionnaireName: name,
      }),
    });
    setIsPicking(false);
  }

  function detach() {
    onUpdate({
      // Back to the generic label — leaving the old questionnaire's name on a step that no longer
      // asks it would be worse than a generic one.
      displayName: 'Questionnaire',
      settings: writeQuestionnaireStepSettings(step.settings, {
        questionnaireId: null,
        questionnaireName: null,
      }),
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray66">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading questionnaires…
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-1">
      <div>
        <h3 className="text-base font-semibold tracking-tight text-black-logo">Questionnaire</h3>
        <p className="mt-1 text-sm text-gray66">
          The questions this step asks the applicant. Only published questionnaires can be attached.
        </p>
      </div>

      {error && (
        <Notice tone="error">
          We couldn't load your questionnaires. Reload the page, or try again in a moment.
        </Notice>
      )}

      {settings.questionnaireId && !isPicking ? (
        <>
          <div className="flex items-center gap-3 rounded-lg border border-grayEF p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <ClipboardList className="h-5 w-5 text-primary" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              {/* The name comes from the list rather than the step's settings: renaming a
                  questionnaire would otherwise leave every workflow showing the old one. */}
              <p className="truncate text-sm font-medium text-black-logo">
                {attached?.name ?? settings.questionnaireName ?? 'Untitled Questionnaire'}
              </p>
              <div className="mt-1 flex items-center gap-3">
                {attached ? (
                  <>
                    <span className="text-xs text-gray66">
                      {attached.sectionCount} section{attached.sectionCount === 1 ? '' : 's'} ·{' '}
                      {attached.fieldCount} field{attached.fieldCount === 1 ? '' : 's'}
                    </span>
                    <QuestionnaireFeatureIcons fieldTypes={attached.fieldTypes} />
                  </>
                ) : null}
              </div>
            </div>
            {!readOnly && (
              <div className="flex shrink-0 items-center gap-1">
                <IconButton label="Change questionnaire" onClick={() => setIsPicking(true)}>
                  <Pencil className="h-4 w-4" />
                </IconButton>
                <IconButton label="Remove questionnaire" danger onClick={detach}>
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              </div>
            )}
          </div>

          {/*
            Two states worth flagging, because both mean applicants will not see what the builder
            thinks they will:

            • the questionnaire was deleted or belongs to another tenant — the step points at
              nothing and the applicant would reach an empty screen;
            • it was unpublished AFTER being attached — the pick list only offers published ones,
              but nothing stops someone archiving it afterwards.
          */}
          {!attached ? (
            <Notice tone="error">
              This questionnaire no longer exists. Pick another one, or remove the step.
            </Notice>
          ) : attached.status !== 'Active' ? (
            <Notice tone="warning">
              This questionnaire is {attached.status.toLowerCase()}, not published. Publish it
              before this workflow goes live, or the applicant will have nothing to answer.
            </Notice>
          ) : attached.fieldCount === 0 ? (
            <Notice tone="warning">
              This questionnaire has no fields yet, so the applicant would see an empty form.
            </Notice>
          ) : null}
        </>
      ) : published.length === 0 ? (
        <div className="rounded-lg border border-dashed border-grayEF p-6 text-center">
          <ClipboardList className="mx-auto h-6 w-6 text-placeholder" aria-hidden="true" />
          <p className="mt-2 text-sm font-medium text-black-logo">
            {all.length === 0
              ? 'No questionnaires yet'
              : taken.size > 0
                ? 'All published questionnaires are already used'
                : 'Nothing published yet'}
          </p>
          <p className="mt-1 text-xs text-gray66">
            {all.length === 0
              ? 'Build a questionnaire, publish it, then attach it here.'
              : taken.size > 0
                ? 'Every published questionnaire is already attached to another step in this workflow. Build another one, or remove this step.'
                : `You have ${all.length} questionnaire${all.length === 1 ? '' : 's'}, but none is published. Publish one to attach it.`}
          </p>
          <button
            type="button"
            onClick={createAndOpen}
            disabled={readOnly || createQuestionnaire.isPending}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#0038CC] disabled:opacity-50"
          >
            {createQuestionnaire.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add Questionnaire
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {published.map((q) => (
            <button
              key={q.id}
              type="button"
              disabled={readOnly}
              onClick={() => attach(q.id, q.name)}
              className={cn(
                'flex items-center gap-3 rounded-lg border border-grayEF p-3 text-left transition-colors',
                readOnly ? 'cursor-default' : 'hover:border-primary hover:bg-[#F9FAFB]',
              )}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <ClipboardList className="h-4 w-4 text-primary" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-black-logo">{q.name}</span>
                <span className="block text-xs text-gray66">
                  {q.sectionCount} section{q.sectionCount === 1 ? '' : 's'} · {q.fieldCount} field
                  {q.fieldCount === 1 ? '' : 's'}
                </span>
              </span>
            </button>
          ))}

          {isPicking && (
            <button
              type="button"
              onClick={() => setIsPicking(false)}
              className="self-start text-xs font-medium text-gray66 transition-colors hover:text-black-logo"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/*
        Always available, including when a questionnaire is already attached — which is the state an
        admin is in when they discover none of the published ones fit and they need another. Before,
        this shortcut existed only in the empty state, so the moment anything was attached the way out
        of the panel disappeared.

        A quiet secondary action, not a second primary button: attaching one of the existing
        questionnaires is the common path, and creating a new one is the exception.
      */}
      {!readOnly && (
        <div className="flex items-center justify-between gap-3 border-t border-grayEF pt-3">
          <button
            type="button"
            onClick={createAndOpen}
            disabled={createQuestionnaire.isPending}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary transition-colors hover:underline disabled:opacity-50"
          >
            {createQuestionnaire.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add Questionnaire
          </button>

          {/* The list, for editing or publishing an existing one rather than making another. */}
          <Link
            to="/questionnaires"
            className="text-xs font-medium text-gray66 transition-colors hover:text-primary"
          >
            Manage all
          </Link>
        </div>
      )}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'warning' | 'error'; children: React.ReactNode }) {
  return (
    <p
      className={cn(
        'flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs leading-relaxed',
        tone === 'error' ? 'bg-[#FDECEC] text-[#8A2020]' : 'bg-[#FFF7E8] text-[#8A5A00]',
      )}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

function IconButton({
  label,
  danger,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
        danger
          ? 'text-destructive hover:bg-red-50'
          : 'text-gray66 hover:bg-grayEF hover:text-black-logo',
      )}
    >
      {children}
    </button>
  );
}
